/**
 * ⭐⭐ **THE CONSOLE'S ONE NAVIGATION SURFACE** — R9 **N-2 / N-5**, `02-NAV-AND-PORT.md` §2–§8.
 *
 * The console had **three** surfaces answering one question (a top-bar section strip, this rail, and
 * an app-scope tab strip). It now has one: a left rail carrying **every** destination, with the app
 * you are working in expressed as a scope control at the top plus a visually-tied scoped group
 * beneath it. The owner: *"I would … put the current 'tabs' on an individual app page in the LHM with
 * a nice looking header in that LHS menu that shows me which app I've chosen."*
 *
 * ## ⭐ RENAMED FROM `SectionRail`, AND ONLY THE NAME MOVED — **N-9**
 *
 * *"Rename what a human reads; never what a gate names."* `SectionRail`'s name and docblock had become
 * false — it is no longer "the rail for the active section" — so the file and the symbol renamed.
 * ⛔ **Every `data-testid` is byte-identical**: the root is still `section-rail` and every link is
 * still `nav-${item.id}`. `routing-branches.test.tsx` (app-scoped hrefs) and `doorless-panes.test.tsx`'s
 * G5 regression guard name `nav-app-*`, and **their subjects are unchanged** by moving those links from
 * a strip back into a column. ⇒ do not "tidy" a test id to match the new component name.
 *
 * ## ⛔ THE SCOPED GROUP IS A SIBLING REGION — TWO `<nav>` LANDMARKS (N-5, ruled)
 *
 * ```
 * <div data-testid="section-rail">                    ← layout only. NO landmark role.
 *   <div data-testid="rail-scope">    …chip…          ← iff an app is in scope
 *   <div data-testid="rail-scroll">                   ← the ONLY scrolling element in the rail
 *     <nav data-testid="rail-scoped" aria-labelledby> ← LANDMARK 1, iff an app is in scope
 *     <nav data-testid="rail-global" aria-label="Console">  ← LANDMARK 2, always
 *   <div data-testid="rail-footer">   …single-app note…
 * ```
 *
 * ⭐ **Two landmarks is what makes the relationship STATABLE rather than washed.** A screen-reader
 * user's landmark menu reads *"App · analytics-ingest navigation"* and *"Console navigation"* — two
 * peers, which is exactly the fact. One nav with an inner wash would say nothing; one nav NESTED in
 * another would assert containment that is false. **You reach Pod logs *instead of* All apps, not
 * *through* it**, so nesting the app's screens under the catalogue entry claims a parent relationship
 * that does not exist — which is the converse of the mistake the design reference already fixed by
 * moving `All apps` OUT of the scoped block.
 *
 * ⛔ **THE ROOT IS A `<div>`, NOT AN `<aside>`.** An `aside` is a *complementary* landmark; wrapping
 * two navigation landmarks in one adds a third landmark that names nothing and that AT users must step
 * through.
 *
 * ⛔ **GROUP LABELS ARE `<div>` + `aria-labelledby`, NOT `<h2>`.** The rail is persistent chrome on
 * every screen, so five `<h2>`s injected into every page's outline makes heading navigation useless for
 * reading the actual page — which is the thing heading navigation is for. The named lists plus two
 * landmarks carry the structure.
 *
 * ⛔ **NO `role="tab"` / `role="tablist"` / `aria-selected` ANYWHERE.** A rail of navigation links is
 * not a tablist: activating a rail item changes the *page*, while activating a tab changes a *view of
 * the same subject*. The APG pattern and the roving tabstop that comes with it are for the second —
 * see `PaneTabStrip`, which is that second thing and keeps them.
 *
 * ⛔ **NO ROVING TABSTOP AND NO ARROW-KEY HANDLER.** Every rail link is in the document tab order. A
 * roving tabstop exists to *reduce* tab stops inside a composite widget; a `<nav>` of links is not a
 * composite, and a roving tabstop would mean an operator cannot Tab from the rail into content without
 * first arrowing to the end. The cost, stated: up to 42 tab stops before `<main>` (28 global
 * entries, 13 app-scoped, and the Settings button), 36 with Settings closed. The sanctioned answer
 * is a skip link (WCAG 2.4.1) and **this package already ships one** (`SectionLayout`, `#main`, first
 * focusable, with `tabIndex={-1}` on `<main>` so focus really moves). ⛔ Adding arrow keys to "fix" the
 * tab-stop count is the plausible-looking wrong edit; the mechanism that solves it is already on screen.
 *
 * ## ⚠️ THE ACCEPTED COST, stated rather than discovered
 *
 * The global groups shift down by the height of the scope region + scoped group when an app is
 * selected, so "Tenants" is not always at the same y. That is accepted: the shift happens only on a
 * navigation *into or out of* app scope — never mid-task — and it is the honest expression of *"this
 * group exists because an app is selected."* Linear, Vercel and GitHub all do the same with
 * repo/project-scoped nav above org nav.
 *
 * One section collapses: Settings, closed by default, and the rail has no overflow menu (plan
 * 2026-10-14-console-nav-rail, D1). The other five sections stay open, because a closed group hides
 * its pages. `rail-scroll` is the only scrolling element; the scope region and the footer are `shrink-0`.
 *
 * ⛔ **THE RAIL IS NOT RESIZABLE — N-7.** Its contents are a fixed, known label set, so there is
 * exactly one correct width and §7.3 computes it. A persisted width would be a **fifth** guarded-
 * `localStorage` site (`SplitDetailLayout.tsx:22` already flags four as a finding) answering a question
 * nobody asked, and it would let an operator shrink the rail until destinations truncate — a
 * self-inflicted version of the defect the width derivation exists to prevent. ⇒ **no
 * `zion.cpadmin.rail.*` key, nothing to persist, and do NOT reuse `SplitDetailLayout`'s divider.**
 *
 * ⛔⛔ **NEVER PORT `.rail { display: none }` FROM THE REFERENCE — N-7.** It is correct in a static
 * artifact with nowhere to put a drawer and catastrophic here: the console becomes unnavigable at
 * tablet width. The `<1024px` drawer is a separate stage; until it lands the rail **stays visible and
 * cramped**. It never disappears.
 *
 * ## ⭐⭐ THE WIDTH IS `w-60` (240px), RE-DERIVED — NOT INHERITED, AND NOT THE PLAN'S 264px
 *
 * `02-NAV-AND-PORT.md` §7.3 derived **264px** from `Fixtures, services & pods` (162.4px, the longest
 * shipped label, and app-scoped so it also paid the 14px scoped inset). ⛔ **The owner then renamed
 * that label to `Fixtures` (2026-08-07), which SUPERSEDED the 264px ruling** and required a
 * re-derivation rather than an adjustment — subtracting the old label's pixels would be arithmetic
 * presented as measurement.
 *
 * Re-measured 2026-08-07, headless Chrome, `--font-ui` at `--text-xs` (14px), all **31** destinations
 * of that day (24 global + 7 scoped, including the 12 not yet ported). The labels added since are
 * shorter than `Observability retention` (23 characters), so the floor below holds by character count.
 * Controls: a 100px box measured 100.0000, and
 * the plan's own published 162.4px for the retired label reproduced **exactly**, so this is the same
 * instrument.
 *
 * ```
 *   widest overall   Observability retention   146.7px  (23 chars, GLOBAL)
 *   widest scoped    History & uptime          108.2px  (16 chars)
 *
 *   global item chrome = 16 (rail-scroll px-2) + 2 (item border-l) + 10 (item pl)
 *                      + 15 (icon) + 10 (gap) + 8 (item pr)                        = 61px
 *   scoped item chrome = 61 + 4 (scoped ml) + 2 (scoped border-l) + 8 (scoped pl)   = 75px
 *
 *   floor = max(61 + 146.7, 75 + 108.2) = max(207.7, 183.2)                = 207.7px
 * ```
 *
 * ⇒ `w-60` = **240px** leaves **32.3px of slack = 22.0% of the binding label**, against the plan's own
 * acceptance bar (it rejected 240 at 1.6% and 248 at 6.5%, and recommended 264 at 16.4%). ⭐ **So the
 * owner's copy change paid for the rail: the widening is no longer needed and no screen loses 24px.**
 *
 * ⚠️ **Slack is not fat.** It absorbs Windows Segoe UI (wider than macOS system-ui) and one browser
 * zoom step — the two reasons §7.3 wanted 16% rather than a hairline fit. ⛔ Do not spend it: at 208px
 * (`Observability retention` + 61px = 207.7) the fit is **0.3px**, and one zoom step truncates a
 * destination, which is the hidden-destination defect the rail exists to remove.
 */
import { useRef, useState } from 'react';
import { Link, NavLink, matchPath, useLocation } from 'react-router';
import {
  Activity,
  Bell,
  Bot,
  Boxes,
  Building2,
  ChevronRight,
  Cpu,
  Eye,
  FileText,
  Fingerprint,
  Gauge,
  GitBranch,
  GitCompare,
  HeartPulse,
  KeyRound,
  KeySquare,
  Layers,
  LayoutDashboard,
  Library,
  LineChart,
  Lock,
  Package,
  Radio,
  Receipt,
  LayoutGrid,
  ScrollText,
  Settings,
  ShieldCheck,
  Server,
  ServerCog,
  Stethoscope,
  Timer,
  UserCog,
  Users,
  Users2,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import { SECTION_LABELS, SECTION_ORDER, type ShellSection } from './manifest';
import { ActionMenu, useCloseMenu } from './ActionMenu';
import { Button } from './Button';
import { TYPEAHEAD_ATTR } from './UserMenu';
// Author names show through `displayUntrustedName`, which caps and escapes them.
// No server control escapes them (#2230), so this is the only control on the console read path.
import { displayUntrustedName } from '../platform/untrusted-text';

const ICON_SOURCE = {
  LayoutDashboard,
  /* `access-library-rights` and `app-libraries` (2026-10-12-shared-libraries, WS-13). Registered in
     the same change as the two manifest entries; the global and the scoped Apps groups draw it once each. */
  Library,
  /* ⭐ `app-compute` (40-ui CG-6, Layer C). ⛔ Registered here in the SAME change as the manifest
     entry: `ICONS` is `Object.assign(Object.create(null), ICON_SOURCE)` and frozen, so this is the
     only editable place, and an unregistered key renders `LayoutDashboard` with no error, no warning
     and no test failure. */
  Cpu,
  LayoutGrid,
  Building2,
  Server,
  ServerCog,
  GitBranch,
  // ⭐ `releases-adoption` — the comparison glyph, which is that screen's whole argument: desired and
  // running as two columns that are allowed to disagree. ⛔ Registered here in the SAME change as the
  // manifest entry; an unregistered key renders `LayoutDashboard` and complains to nobody.
  GitCompare,
  KeyRound,
  /* `platform-fleet-health` (WS-17 of 2026-10-27-dp-telemetry-residency): the problems of every data
     plane. Registered in the same change as the manifest entry. */
  HeartPulse,
  ShieldCheck,
  Boxes,
  ScrollText,
  Users,
  Activity,
  Gauge,
  Bell,
  Package,
  /*
   * ⭐ The two glyphs WS-7's deck screens add, registered in the SAME change as the
   * manifest objects that name them. ⛔ An unregistered key renders `LayoutDashboard`
   * with no error, no warning and no test failure except `manifest-icons.test.ts`.
   *
   * `Layers`  — `platform-queues`, and it is the deck's own choice (`mockups/shell.js:232`
   *             `{ id:'queues', label:'Queues & DLQ', icon:'layers' }`).
   * `Radio`   — `releases-channels`, likewise the deck's (`shell.js:306`,
   *             `{ id:'rings', label:'Release channels', icon:'radio' }`).
   * ⚠️ `ScrollText` is NOT re-added for `platform-cp-logs` — it is already registered
   *   for `app-logs`, and sharing a glyph across sections has precedent (`Activity`
   *   serves `platform-in-flight` and `app-history`).
   */
  Layers,
  Radio,
  /*
   * ⭐ The two glyphs the DATA-PLANE scope adds (R9 N-11), registered in the SAME change as the
   * manifest projection that names them — an unregistered key renders `LayoutDashboard` with no
   * error, no warning and no test failure.
   *
   * ⚠️ `LineChart` was already the metrics glyph on both of the doors this change deletes
   * (`DataPlaneOverviewTab`, `DataPlaneDiagnosticsTab`), so the destination inherits the icon the
   * operator already associates with it rather than being given a new one.
   */
  LineChart,
  Stethoscope,
  /*
   * ⭐ R9 N-e — the five glyphs the seventeen-screen port adds, registered in the SAME change as the
   * manifest objects that name them. ⛔ An unregistered `iconKey` renders `LayoutDashboard` with no
   * error, no warning and no test failure except `manifest-icons.test.ts`, which is why this is not a
   * follow-up.
   *
   * ⚠️ **MEASURED against this map, not copied from the plan's table** — the plan marked several of
   * these "verify" precisely because it was written against a file four implementers were editing.
   * Already present and therefore NOT re-added: `GitBranch` (Ring board, shared with `app-rollout`),
   * `KeyRound` (Grants, shared with `access-reach`), `LineChart` (Grafana, shared with `dp-metrics`).
   *
   * `Workflow` is the glyph of `platform-channels`. The Launch graph shared it until S6 of plan
   * 2026-10-14-app-pools-runtime-deletion deleted that screen.
   */
  Workflow,
  Users2,
  FileText,
  Settings,
  Timer,
  /*
   * ⭐ THE FINAL-CUTOVER GLYPHS (WS-6 · 06a/06d/06e), registered by the STEWARD in the SAME change as
   * the manifest objects that name them. ⛔ An unregistered `iconKey` renders `LayoutDashboard` with
   * no error, no warning and no test failure except `manifest-icons.test.ts`.
   *
   * ⚠️ **MEASURED against this map by set-difference, not copied from a plan table.** Already present
   * and therefore NOT re-added: `Settings` (`app-config`, shared with the `settings` SECTION — the
   * rail renders one section at a time so they never co-render), `Package` (`app-build`, shared with
   * `releases-versions`), `GitCompare` (`app-adoption`, shared with `releases-adoption`), `ScrollText`
   * (`platform-cp-logs`, shared with `app-logs`), and `Layers`/`Radio`, which WS-7 pre-registered for
   * `platform-queues` / `releases-channels`.
   *
   * `Fingerprint` — `settings-idp-providers`. `UserCog` — `settings-scim-clients`. ⚠️ Not `Users2` or
   *   `ShieldCheck`: both are taken by entries that would sit in the same rail group.
   * `KeySquare`   — `settings-oauth-clients`. ⚠️ Not `KeyRound`, which already serves `access-grants`
   *   and `access-reach`; a third screen on the same glyph stops the glyph meaning anything.
   * `Receipt`     — `platform-billing`, the Cost page; the glyph the retiring workspace used, so
   *   the operator carries the association across.
   */
  Fingerprint,
  UserCog,
  KeySquare,
  /*
   * `Bot` draws `settings-machine-identities` (plan 2026-10-21-release-on-zion-build, WS-17): the
   * non-human callers. No other `settings` entry draws it, so the section never shows one glyph on
   * two rows. The other `settings` glyphs:
   * - Settings
   * - Timer
   * - Fingerprint
   * - Lock
   * - UserCog
   * - KeySquare
   */
  Bot,
  /*
   * `Receipt` — `platform-billing`, the one Cost page, whose four tabs (Ledger, GCP bill, Scale events,
   * Rate card) share it. It is the glyph the retiring billing workspace used, so the operator's
   * association carries across. The other eight `platform` entries (Building2, Server, Gauge,
   * Activity, Bell, Workflow, ScrollText, Layers) do not use it.
   */
  Receipt,
  /*
   * ⭐ **`Eye` — `app-availability` (#2568), "who can SEE this app".**
   *
   * ⛔ Checked against the seven other `apps` entries it is co-rendered with — Package,
   * GitCompare, GitBranch, Workflow, Radio, Users, KeyRound, ShieldCheck, Cpu, ScrollText — no
   * collision inside the section, which is the property that matters: two rail rows drawing the
   * same glyph in the same group are indistinguishable at a glance.
   *
   * ⚠️ NOT `ShieldCheck` or `KeyRound`, the two nearest candidates. Availability is not an
   * authorization boundary — the controller's own docblock says so outright (*"a licence is NOT a
   * security boundary; it governs what people are SHOWN"*) — and a lock glyph would teach the
   * operator the opposite of the one thing this screen exists to explain.
   */
  Eye,
  /*
   * `Lock` — `releases-private-channels` (private-release-channels WS-5). Here the lock tells the
   * truth that `Eye`'s paragraph above keeps it from telling on `app-availability`: a tenant that
   * has not accepted a share never receives a private channel's builds (L-4, L-6). Checked against
   * the five other `releases` entries it is co-rendered with (Package, GitCompare, GitBranch,
   * Workflow, Radio): no collision in the section. Registered in the same change as the manifest
   * entry, because an unregistered key renders `LayoutDashboard` with no error.
   * `settings-identity-token` draws it too (feature 2026-10-22-cp-identity-token, R-014): on GKE
   * the issuer's private key stays in Cloud KMS. A local CP signs with the software key. That entry
   * is the one `settings` entry that uses it.
   */
  Lock,
};

/**
 * The lucide components a manifest `iconKey` may name.
 *
 * ⛔ An explicit MAP, not a dynamic `lucide-react` lookup: a dynamic import of the whole icon set
 * defeats tree-shaking and pulls ~1000 components into the bundle. Each screen adds its icon here
 * alongside its manifest object.
 *
 * ⛔ DELIBERATELY NULL-PROTOTYPE, and this is the same defect class as R6 security finding S-2 (the
 * `ui` allowlist admitting `Object.prototype` keys via `in`): **indexing a plain object with a string
 * you did not enumerate.** `ICONS[item.iconKey]` takes an arbitrary string, so on an ordinary object
 * literal `ICONS['toString']` returns an INHERITED FUNCTION — truthy, so the `?? LayoutDashboard`
 * fallback never fires and React is handed `Function.prototype.toString` to render. Measured:
 *
 * ```
 *   toString      plain=function   nullProto=undefined
 *   constructor   plain=function   nullProto=undefined
 *   LayoutGrid    plain=number     nullProto=number     <- control: the map still works
 * ```
 *
 * ⚠️ LATENT, not exploitable: `iconKey` is manifest-controlled, not user-controlled. It is fixed on
 * consistency and near-zero cost, not severity.
 *
 * ⭐ The repo already decided this axis — see the verbatim precedent at
 * `cloud/platform-sdk/contracts/src/grant-principal.ts:40-49`: *"Deliberately null-prototype… on an
 * ordinary object literal `map['toString']` is an inherited function, not `undefined`, so a `?? null`
 * fallback would hand back a truthy non-`GrantPrincipalType`… Do not 'simplify' this back to a bare
 * object literal."* ⛔ The same instruction applies here.
 */
export const ICONS: Readonly<Record<string, LucideIcon>> = Object.freeze(
  Object.assign(Object.create(null) as Record<string, LucideIcon>, ICON_SOURCE),
);

/** A resolved rail link. `to` is basename-relative and already app-scoped where relevant. */
export interface RailItem {
  readonly id: string;
  readonly to: string;
  readonly displayName: string;
  readonly iconKey: string;
  /** `end` for a link whose path is a prefix of its siblings', so it does not stay lit. */
  readonly end: boolean;
  /**
   * Which rail group this item belongs to.
   *
   * ⛔ **THIS REPLACED `railGroup?: string`, and it is not a rename** (R9 Q1). `railGroup` was an
   * optional free-string divider whose ORDER came from first appearance in the item list, so a
   * manifest reorder silently reshuffled the rail. A `ShellSection` is a closed union ordered by
   * `SECTION_ORDER`, so the grouping is total and the ordering has exactly one source.
   */
  readonly section: ShellSection;
  /**
   * An optional count beside the label (#2621).
   *
   * ⛔ Absent when the number was not collected. There is no arm for *"we could not tell"*, and
   * that is the rail's shape rather than an omission: a rail item is two lines wide and an absence
   * needs a sentence. The surface that states the absence is the catalogue's fourth tile.
   *
   * ⛔ **The rail takes no decision about what a count MEANS.** `AppHost`'s `railItems` producer
   * sets this field, and only from a `ready` summary with a non-zero number — the same rule that
   * keeps `to` built once, in the producer, and never here.
   *
   * `srLabel` carries the meaning for a screen reader (*"3 apps not offered to any tenant"*); the
   * visible number is for everyone else and is `aria-hidden`, so the digit is announced once.
   *
   * `tone` is the producer's choice and absent means neutral. The rail maps it to a pill class and
   * reads nothing else into it.
   */
  readonly count?: {
    readonly value: number;
    readonly srLabel: string;
    readonly tone?: RailCountTone;
  };
  /**
   * The id of the item this one sits under, indented. `AppHost` copies the manifest's
   * `GlobalEntry.navParent` here. The rail shows a parent's children while the current page is the
   * parent's or a child's. Absent: the item sits at the top level of its group.
   */
  readonly parentId?: string;
}

/**
 * Whether the rail lights `item` on `pathname`: its own `to` by the rule `NavLink` uses (no regard to
 * case, `end` for an exact match). Pure. The disclosure of a collapsible group and a rail parent read
 * it to know that the current page is in the group or under the parent.
 */
export function railItemLit(item: RailItem, pathname: string): boolean {
  return matchPath({ path: item.to, end: item.end, caseSensitive: false }, pathname) !== null;
}

/** A top-level rail item and the items indented under it, in the order the items arrive. */
export interface RailBranch {
  readonly item: RailItem;
  readonly children: readonly RailItem[];
}

/**
 * Split a group's items into top-level items, each with its children. An item whose `parentId`
 * names no item in the list sits at the top level, so a parent the viewer cannot see never hides
 * its children. Pure.
 */
export function railBranches(items: readonly RailItem[]): readonly RailBranch[] {
  const ids = new Set(items.map((item) => item.id));
  const nested = (item: RailItem): boolean => item.parentId !== undefined && ids.has(item.parentId);
  return items
    .filter((item) => !nested(item))
    .map((item) => ({ item, children: items.filter((child) => nested(child) && child.parentId === item.id) }));
}

/** A labelled cluster of rail items — one per visible section, in `SECTION_ORDER`. */
export interface RailGroup {
  readonly section: ShellSection;
  readonly label: string;
  readonly entries: readonly RailItem[];
}

/**
 * Partition items into one labelled group per section, **in `SECTION_ORDER`**, dropping sections with
 * no visible entry.
 *
 * ⛔ **IT ITERATES `SECTION_ORDER` AND FILTERS — it does NOT walk the items and discover groups.**
 * That is the whole delta from the retired `groupRailEntries`, which ordered groups by whichever entry
 * happened to be declared first: the rail's group order was therefore an emergent property of manifest
 * composition, and appending a screen could reshuffle the whole rail with nothing failing. Here the
 * order is declared in one place and the items cannot influence it.
 *
 * ⚠️ A section with zero visible entries yields NO group rather than an empty one. An empty labelled
 * group is a heading over nothing — the same "this is broken, or still loading" signal that
 * `SectionLayout.tsx:420-425` measured for an entry-less rail, at group scale.
 *
 * ⛔ Pure and exported so both arms are asserted directly rather than inferred from a rendered tree.
 */
export function groupRailBySection(items: readonly RailItem[]): readonly RailGroup[] {
  const out: RailGroup[] = [];
  for (const section of SECTION_ORDER) {
    const entries = items.filter((i) => i.section === section);
    if (entries.length > 0) out.push({ section, label: SECTION_LABELS[section], entries });
  }
  return out;
}

/**
 * Two-letter mark from an app name — the rail's app-scope glyph. It reads the raw name and keeps
 * only letters and digits, so a control or bidi code point never reaches the mark (plan G8, R-12).
 */
export function appInitials(name: string): string {
  const words = name
    .split(/\s+/)
    .map((word) => word.replace(/[^\p{L}\p{N}]|[\u115F\u1160\u3164\uFFA0]/gu, ''))
    .filter(Boolean);
  // A single word contributes its first TWO letters ("Crawler" → "CR") rather than one, so the
  // mark is a consistent size; two or more words contribute one letter each ("Perf Lab" → "PL").
  // An empty name yields an empty mark rather than a placeholder character — the block still
  // renders the real name beside it, so an invented glyph would be the only lie on screen.
  if (words.length === 0) return '';
  if (words.length === 1) return Array.from(words[0]).slice(0, 2).join('').toUpperCase();
  return `${Array.from(words[0])[0]}${Array.from(words[1])[0]}`.toUpperCase();
}

/**
 * ⛔ **M4 — `aria-current="page"` vs `aria-current="true"`, and the difference is a factual claim.**
 *
 * `aria-current="page"` means specifically *"this link's destination IS the page you are on"*.
 * On `/platform/tenants/:tenantId` the Tenants rail entry is `end: false` so it STAYS LIT — correct
 * and load-bearing for R7-DEC-4 — but `NavLink` writes `page` whenever it matches, so the document
 * simultaneously claimed three different things were the current page, two of them links to a
 * DIFFERENT page. A screen-reader user was told the current page was `/platform/tenants` while
 * reading a tenant detail screen.
 *
 * The honest value for an ancestor-highlighted item is `"true"` — *"current item within its
 * container"*. ⚠️ `NavLink`'s `aria-current` prop changes the VALUE, not the CONDITION (it still
 * emits nothing when inactive), which is exactly what is wanted here.
 *
 * ⛔ Exported and pure so both arms are asserted directly rather than inferred from a rendered tree.
 */
export function railAriaCurrent(pathname: string, to: string): 'page' | 'true' {
  // The router matches with no regard to case, so `/Settings/x` is the page `/settings/x`.
  return pathname.toLowerCase() === to.toLowerCase() ? 'page' : 'true';
}

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * ⭐⭐ THE RAIL'S LOOK-AND-FEEL, IN ONE PLACE — R9 **N-13**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Owner: *"make sure the LHS menu is correctly pulled out as a component so if we want to change the
 * LAF of it for all pages, it is in one place."*
 *
 * **Measured 2026-08-07, before this block: 25 inline `className=` sites and exactly ONE extracted
 * constant** (`GROUP_LABEL_CLASS`). ⚠️ The spine said 19 and had said so for a day; the number is
 * recorded here because a stale count is what let this look already-done.
 *
 * The house pattern is the opposite of 25 literals: `SplitDetailLayout` exports
 * `OPTION_BASE_CLASS` / `OPTION_SELECTED_CLASS` / `OPTION_UNSELECTED_CLASS`; `FilterSection` exports
 * `FIELD_CLASS`.
 *
 * ## ⭐ WHY THIS IS CORRECTNESS AND NOT TIDINESS — `SplitDetailLayout`'s own scar
 *
 * Its focus ring lives in the shared **base** because the first draft put it on the unselected arm
 * only, which dropped the ring from *exactly the option the keyboard lands on first*: *"A per-arm
 * class list makes that omission invisible; a base class makes it impossible."* ⛔ This rail now has
 * a THIRD scope arriving (the data plane) on top of the app scope and the six global groups, so
 * that class of omission is not hypothetical — it is what inline literals were about to create here.
 *
 * ⇒ ⛔ **`RAIL_ITEM_BASE_CLASS` CARRIES THE FOCUS RING AND BOTH ARMS CONCATENATE ONTO IT.** Never
 * move a focus, hover or transition declaration into `SELECTED`/`UNSELECTED`.
 *
 * ⚠️ **This is centralising the TREATMENT, not widening the BOUNDARY** (N-13's own caveat). The rail
 * is chrome — mounted once by the shell, never page-authored (`COMPONENT-LIBRARY.md` §1). ⛔ Do not
 * promote it to `shell/ui/`, and do not give a pane a reason to import these constants; they are
 * exported so a **test** can assert one treatment reaches every scope, which is the property that
 * stops the three scopes drifting apart.
 */

/** The rail column itself. `w-60` = 240px, re-derived — see this file's header for the measurement. */
export const RAIL_ROOT_CLASS =
  'flex h-full w-60 shrink-0 flex-col border-r border-border-subtle bg-chrome';

/**
 * ⛔ THE ONLY SCROLLING ELEMENT IN THE RAIL. `min-h-0` is what lets it shrink below its content.
 * `relative` contains the absolute `sr-only` spans of the rail items, so a tall rail cannot stretch
 * `<html>` and scroll the document (the same reason as `SCROLL_REGION_CLASS`).
 */
export const RAIL_SCROLL_CLASS = 'relative min-h-0 flex-1 overflow-y-auto px-2 pt-2 pb-4';

/**
 * The focus ring of every rail link, through `RAIL_ITEM_BASE_CLASS`. The Settings button is a `Button`
 * and takes the same literal from `BUTTON_BASE_CLASS`; a chrome test holds the two equal.
 */
export const RAIL_FOCUS_RING_CLASS =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring';

/**
 * ⛔ **THE SHARED BASE — every item in every group and every scope.** Focus ring and transition live
 * HERE and must never move into an arm. @see the block comment above for the incident that decided it.
 */
export const RAIL_ITEM_BASE_CLASS = `flex items-center gap-2.5 rounded-lg py-1.5 pr-2 text-xs leading-tight transition-colors ${RAIL_FOCUS_RING_CLASS}`;

/**
 * ⛔ **FIVE CHANNELS, NONE OF THEM COLOUR ALONE** — a 3px accent bar, an 18% accent wash, the accent
 * glyph, `font-semibold`, and `aria-current`. @see RailLink for why the LABEL is never recoloured.
 *
 * ⛔ **3px bar + 9px padding == 2px border + 10px padding** (see the resting arm), so the label's
 * x-position does not shift on selection. Change one number and you must change the other.
 */
export const RAIL_ITEM_SELECTED_CLASS =
  'border-l-[3px] border-accent bg-accent/18 pl-[9px] font-semibold text-primary';

/**
 * The resting arm. ⛔ The 2px TRANSPARENT border is load-bearing — it reserves the selected bar's
 * width so selection does not nudge the label. ⛔ Hover is `--color-chrome-hover`, a `color-mix`,
 * NEVER a surface token (N-6 D-A: the reference's `--color-surface-muted` computes 1.0000:1 over
 * `--color-chrome` in light, i.e. no hover state at all).
 */
export const RAIL_ITEM_UNSELECTED_CLASS =
  'border-l-2 border-transparent pl-2.5 font-medium text-secondary hover:bg-chrome-hover hover:text-primary';

/**
 * A parent whose children show: the resting geometry, in the primary text, with no fill. The child
 * that is the current page carries the selected treatment, so one row shows where you are.
 */
export const RAIL_ITEM_OPEN_PARENT_CLASS =
  'border-l-2 border-transparent pl-2.5 font-semibold text-primary hover:bg-chrome-hover';

/**
 * The list of a parent's children. The left margin is the parent's icon width plus its gap
 * (15 px + 10 px), so each child label starts under the parent label.
 */
export const RAIL_CHILD_LIST_CLASS = 'mt-0.5 ml-[25px] space-y-0.5';

export const RAIL_ITEM_ICON_CLASS = 'h-[15px] w-[15px] shrink-0';
export const RAIL_ITEM_LABEL_CLASS = 'truncate';

/**
 * The tone of a rail count. The producer picks it: `attention` marks work that waits on an admin,
 * and `neutral` marks every other count. Neither tone is red, because a count carries no verdict.
 */
export type RailCountTone = 'neutral' | 'attention';

/**
 * The pill shape of a rail count (#2621, R-032). `ml-auto` puts it at the far right of the row, and
 * the label's `truncate` lets a long label shrink first, so the pill always stays in view.
 */
export const RAIL_ITEM_COUNT_CLASS =
  'ml-auto inline-flex h-4.5 min-w-4.5 shrink-0 items-center justify-center rounded-full px-1.5 font-mono text-2xs tabular-nums';

/**
 * The fill and the digit colour of each tone. The neutral pill is a pale tint, and the attention
 * pill is a solid accent fill with the `on-accent` digit.
 *
 * The contrast suite measures each pair on the resting, hover and selected grounds, in both modes:
 *
 * 1. Each digit holds 4.5:1 on its own fill.
 * 2. The two fills differ in luminance by 3:1 or more, so the tones differ by more than hue.
 * 3. Each fill stands apart from its ground.
 */
export const RAIL_ITEM_COUNT_TONE_CLASS: Readonly<Record<RailCountTone, string>> = {
  neutral: 'bg-border-subtle/20 text-secondary',
  attention: 'bg-accent text-on-accent',
};

/**
 * The visually hidden sentence beside the count (#2621, R-039).
 *
 * ⛔ **A CONSTANT RATHER THAN THE LITERAL `className="sr-only"` THE DESIGN FENCE WRITES**, and a
 * ratchet in `chrome.test.tsx` is why: *"zero literal className strings survive in NavRail.tsx"*,
 * with a ceiling of 0. An inline `sr-only` would red it, and the ratchet is right — one quick
 * inline class for one more scope is how the other 24 sites got here.
 *
 * `sr-only` is this package's visually hidden class, used by `DataValue` in `data-tone.tsx` and by
 * `AppLoader`.
 */
export const RAIL_ITEM_COUNT_SR_CLASS = 'sr-only';

/** The first section group's outer box, and the list inside every group. */
export const RAIL_GROUP_CLASS = 'pt-2';
export const RAIL_LIST_CLASS = 'space-y-0.5';

/**
 * Every section after the first: 10px, a 1px subtle line, then 6px here plus the title's 4px top
 * padding, so 20px separate the last entry from the title text, with the line in the middle.
 */
export const RAIL_GROUP_DIVIDED_CLASS = 'mt-2.5 border-t border-border-subtle pt-1.5';

/**
 * The section title: sentence case, 14px, bold, `text-primary`. A resting entry is `font-medium
 * text-secondary`, so the title differs from it in weight and colour.
 */
export const GROUP_LABEL_TYPE_CLASS = 'text-xs font-bold text-primary';
export const GROUP_LABEL_CLASS = `px-2 pt-1 pb-1.5 ${GROUP_LABEL_TYPE_CLASS}`;

/**
 * The Settings button's label: the title type, and it grows so the chevron sits at the right. The
 * `micro` size gives 2px above and below, so 2px and 4px here match the static title's 4px and 6px.
 */
export const RAIL_DISCLOSURE_LABEL_CLASS = `flex-1 pt-0.5 pb-1 text-left ${GROUP_LABEL_TYPE_CLASS}`;

/** The disclosure chevron. It turns a quarter on open, and holds still under reduced motion. */
export const RAIL_DISCLOSURE_CHEVRON_CLASS =
  'h-3.5 w-3.5 shrink-0 transition-transform motion-reduce:transition-none';
export const RAIL_DISCLOSURE_CHEVRON_OPEN_CLASS = 'rotate-90';

/** Closed Settings on a Settings page: an accent dot, the selected bar's colour, marks the page. */
export const RAIL_DISCLOSURE_CURRENT_CLASS = 'h-1.5 w-1.5 shrink-0 rounded-full bg-accent';

/** The mono half of the scoped label — the record's own short handle. */
export const RAIL_SCOPED_SUBJECT_CLASS = 'font-mono';

/**
 * ⭐⭐ **TWO SIGNALS, NOT THREE — THE WASH IS GONE, AND IT WAS MEASURABLY THE WEAKEST ONE** (owner,
 * 2026-08-07: *"some weird styling for the 'current' app background, border, edges to fix"*).
 *
 * What ties this block to the chip above it is exactly: a **2px `border-strong` connector** descending
 * from the chip's own left edge, and a **label that NAMES THE RECORD**. ⛔ The connector is
 * `border-strong`, NEVER the accent — the accent is spent entirely on *which destination am I on*.
 *
 * The retired 8% wash measured **1.0747 light / 1.0837 dark** against `--color-chrome` — *fainter
 * than the hover state* (1.1164 / 1.1291), a permanent structural claim rendered weaker than a
 * transient one. It also cost: a selected item's accent glyph at **3.1231** (today **3.5769**, the
 * same as a selected GLOBAL item); a `rounded-lg` pill flush against a square panel edge, cutting two
 * grey notches — the literal artifact the owner saw; three corner treatments on one box; and a 4px
 * asymmetric inset. ⛔ Do not reinstate a fill to "make the group read": **a relationship is not a
 * container.**
 *
 * ⚠️ `ml-1` STAYS and is not decoration: `px-2` (8) + `ml-1` (4) puts the connector at **x=12**, which
 * is exactly the chip's `p-3` — so the rule descends from the chip's own left border.
 */
export const RAIL_SCOPED_GROUP_CLASS = 'mb-2 ml-1 border-l-2 border-border-strong pl-2';

/**
 * The scope chip's container. ⛔ `flex flex-col` and NOT a plain block: `Button`'s base is
 * `inline-flex` (content-sized), so `align-items: stretch` on this column is the ONLY thing making
 * the picker trigger fill the rail's width. `Button` refuses a forwarded `className` by design, so
 * *"give it a width"* is the parent's job.
 */
export const RAIL_SCOPE_CLASS = 'flex shrink-0 flex-col border-b border-border-subtle p-3';

/**
 * The non-interactive chip: the fewer-than-two-choices and list-not-arrived arms.
 *
 * Its box is the box of the picker trigger (`Button` tone `chrome`, size `sm`): the same padding,
 * gap, radius and border tone. So a chip that turns into a picker adds only the chevron, and the rail
 * below it does not move under the cursor.
 */
export const RAIL_SCOPE_STATIC_CLASS =
  'flex w-full items-center gap-1.5 rounded-md border border-border-control px-2.5 py-1 text-left';

export const RAIL_SCOPE_MONOGRAM_CLASS =
  'flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-md bg-accent text-2xs font-bold tracking-tight text-on-accent';

/**
 * ⛔ `text-left` IS LOAD-BEARING AND IT WAS FOUND BY RENDERING, NOT BY READING. A `<button>` has
 * `text-align: center` from the UA stylesheet; the old chip was an `<a>` carrying an explicit
 * `text-left`. Moving to a real menu button silently re-introduced the centring, so the name floated
 * in the middle with the monogram stranded at the far left. ⚠️ Set here rather than on `Button`,
 * because a centred label is correct for an ordinary button — this is a property of a COMPOSITE
 * trigger, not of the primitive.
 */
export const RAIL_SCOPE_TEXT_CLASS = 'min-w-0 flex-1 text-left';
export const RAIL_SCOPE_TITLE_CLASS = 'block truncate text-xs font-semibold text-primary';
export const RAIL_SCOPE_SUBJECT_CLASS = 'block truncate font-mono text-2xs text-muted';

/** One picker row: the shared base, then the current / resting arm. Same base rule as the items. */
export const RAIL_PICKER_ITEM_BASE_CLASS =
  'flex w-full items-center gap-2 px-3 py-1.5 text-xs focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus-ring';
export const RAIL_PICKER_ITEM_CURRENT_CLASS = 'font-semibold text-primary';
export const RAIL_PICKER_ITEM_RESTING_CLASS =
  'text-secondary hover:bg-chrome-hover hover:text-primary';
export const RAIL_PICKER_NAME_CLASS = 'min-w-0 flex-1 truncate';
export const RAIL_PICKER_SUBJECT_CLASS = 'shrink-0 font-mono text-2xs text-muted';
/** A stacked picker row: the name on the first line, the subject under it. Same focus ring. */
export const RAIL_PICKER_STACKED_ITEM_BASE_CLASS =
  'flex w-full flex-col items-stretch px-3 py-1.5 text-xs focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus-ring';
export const RAIL_PICKER_STACKED_NAME_CLASS = 'block truncate';
export const RAIL_PICKER_STACKED_SUBJECT_CLASS = 'block truncate font-mono text-2xs text-muted';
export const RAIL_PICKER_ALL_CLASS = 'flex-1';
export const RAIL_PICKER_SEPARATOR_CLASS = 'my-1 border-t border-border-subtle';

export const RAIL_FOOTER_CLASS = 'shrink-0 border-t border-border-subtle px-3 py-2.5';
export const RAIL_FOOTER_NOTE_CLASS = 'max-w-[100ch] text-2xs leading-snug text-muted';

/**
 * ⭐ **THE SELECTED STATE IS FIVE CHANNELS, AND NONE OF THEM IS COLOUR ALONE** (§7.4): a 3px accent
 * left bar, an 18% accent wash, the icon in the accent, `font-semibold`, and `aria-current`.
 *
 * ⛔ **THE LABEL IS NEVER RECOLOURED TO THE ACCENT.** Two grounds, and the ORDER matters because the
 * first cannot expire (R9 **N-18**):
 *
 * **1. WCAG 1.4.1, use of colour — the rule's real reason.** The claim above is *five channels, none of
 * them colour alone*. An accent label makes the selected state accent-bar + accent-wash + accent-glyph +
 * accent-label, i.e. it collapses **toward colour alone** — which is the defect the five channels exist
 * to prevent. ⭐ This ground is **independent of every token value**, so no recalibration can invalidate
 * it.
 *
 * **2. Legibility, measured.** `--color-primary` on the wash is **13.0092 light / 10.1295 dark**; an
 * accent label would be **4.5336**. That trades 13.0 for 4.53 for **zero added signal**, since the
 * accent already carries three of the five channels.
 *
 * ⚠️⚠️ **THE ORIGINAL JUSTIFICATION WAS A CONTRAST ONE AND IT HAS BEEN RETIRED — recorded, not
 * deleted.** This block used to read *"light accent as text on chrome is **4.1621:1**, under the 4.5
 * floor"*. That was TRUE and is now FALSE: `--color-accent` was **recalibrated on 2026-08-07** (#1213 —
 * `index.css` carries the values and the derivation) and the same pair now measures **4.5336 — above the
 * floor.**
 *
 * ⚠️ **This block deliberately does NOT spell either hex.** A component naming a token literal is the
 * second source of truth the recalibration exists to remove, and `token-parity.test.ts`'s
 * superseded-accent gate reds on the old one anywhere in `src/**` outside the three files whose job is
 * to explain it. ⭐ Caught by that gate on this very docblock, one minute after writing it — which is
 * the gate doing exactly what it was added for, to its own author.
 *
 * ⇒ ⭐ **Fixing the contrast defect removed the stated reason for a rule that is still correct.** Swapping
 * the figure would have left this file justifying the rule with a number that now argues *against* it —
 * worse than either the old bug or no comment at all. ⛔ So the rule rests on 1.4.1, and the retirement
 * is written down: a rule whose original reason silently vanished is one the next author deletes.
 *
 * The accent stays on the bar and the glyph, which answer 1.4.11's **3:1** rather than 1.4.3's 4.5
 * (4.5336 / 6.7034 on chrome; 3.5769 / 4.8940 on the selected wash).
 *
 * ⛔ **THE BAR IS 3px AND THE SCOPED CONNECTOR IS 2px — THAT IS A RELATIONSHIP AND IT TRANSFERS.**
 * They are the only two left edges in the rail and must stay distinguishable at a glance. The resting
 * item carries a 2px TRANSPARENT border so the label's x-position does not shift on selection (3px bar
 * + 9px padding == 2px border + 10px padding).
 *
 * ⛔ **HOVER IS `--color-chrome-hover` — a `color-mix`, NEVER a surface token (N-6).** The reference's
 * `background: var(--color-surface-muted)` computes **1.0000:1** over `--color-chrome` in light — the
 * same colour, so no hover state at all. The mix measures **1.1164 light / 1.1291 dark**.
 * ⚠️ Both figures are `--color-border-subtle` mixes and are therefore **unaffected by the accent
 * recalibration** — checked, not assumed.
 *
 * ⭐ **ONE GROUND, EVERYWHERE — the 2026-08-07 simplification.** A scoped item and a global item now
 * hover, rest and select over the identical `--color-chrome`, because the scoped group carries no
 * fill. ⇒ the figures above are the WHOLE hover story; there is no second set for "inside the scoped
 * block", and the selected glyph's tightest ratio went from **3.1231** (18% accent on the old 8%
 * wash) to **3.5769**. See `index.css` for why the wash is not coming back.
 * ⚠️ That second figure read 3.3417 until the 2026-08-07 accent recalibration; it is a ratio of the
 * accent against a tint of itself, so it moved with the token.
 */
function RailLink({
  item,
  expanded,
  controls,
  nested = false,
}: {
  readonly item: RailItem;
  /** Set on a parent only: whether its children show. */
  readonly expanded?: boolean;
  /** Set on a parent only: the id of its child list, named by `aria-controls` while the list shows. */
  readonly controls?: string;
  /** A child of a parent: no icon, so its label starts under the parent label. */
  readonly nested?: boolean;
}): React.JSX.Element {
  const { pathname } = useLocation();
  /*
   * ⚠️ The `??` fallback STAYS, and it is the wrong half of the fix on its own.
   *
   * At runtime, failing to render a rail item is worse than rendering a wrong icon — a nav entry
   * that throws takes the whole rail with it. But a missing `iconKey` silently renders a PLAUSIBLE
   * dashboard icon and complains to nobody: no error, no warning, no test failure, and "the icon
   * looks a bit odd" is not a bug anyone files.
   *
   * ⛔ So the catch is a BUILD-TIME gate, not a runtime throw: `manifest-icons.test.ts` asserts every
   * manifest `iconKey` is a key of `ICONS`. Belt at build time, suspenders here.
   */
  const Icon = ICONS[item.iconKey] ?? LayoutDashboard;
  const body = (
    <>
      {nested ? null : <Icon className={RAIL_ITEM_ICON_CLASS} aria-hidden />}
      <span className={RAIL_ITEM_LABEL_CLASS}>{displayUntrustedName(item.displayName)}</span>
      {/*
        ⛔ **RENDERED ONLY WHEN THE FIELD IS SET, AND THE RAIL DECIDES NOTHING ABOUT WHEN THAT IS**
        (#2621). `AppHost`'s `railItems` producer sets `count`, and only from a `ready` summary
        with a non-zero number. Five states reach the absent case — a 403, a failed read, a read
        in flight, a read that was never applicable, and a collected zero — and all five must look
        identical here, because a rail item is two lines wide and has no room to say which. A `0`
        rendered for a refused read tells that operator the fleet is fine.

        The digit is `aria-hidden` and the sentence is `sr-only`, so a screen reader announces the
        number once, with its meaning. The sentence comes first and the pill comes last, so the
        pill is the last child and sits at the right edge. The digit carries its own `data-testid`.
        A test then reads the number without the label and the hidden sentence.
      */}
      {item.count !== undefined && (
        <>
          <span className={RAIL_ITEM_COUNT_SR_CLASS}>{item.count.srLabel}</span>
          <span
            aria-hidden="true"
            data-testid={`nav-${item.id}-count`}
            className={`${RAIL_ITEM_COUNT_CLASS} ${RAIL_ITEM_COUNT_TONE_CLASS[item.count.tone ?? 'neutral']}`}
          >
            {item.count.value}
          </span>
        </>
      )}
    </>
  );
  /*
   * One `NavLink` serves every state, so a press on a closed parent keeps the focus on the pressed
   * anchor when its children open. A parent whose children show matches only its own path (`end`),
   * so on a child page the parent carries no `aria-current`; the child that is the page carries it.
   */
  const open = expanded === true;
  return (
    <NavLink
      to={item.to}
      end={open ? true : item.end}
      data-testid={`nav-${item.id}`}
      aria-expanded={expanded}
      aria-controls={open ? controls : undefined}
      // ⛔ M4 — `page` only when this link's destination IS the current path; `"true"` when it is lit
      // as an ANCESTOR. `NavLink` still decides WHETHER to emit it; this decides WHICH value.
      aria-current={railAriaCurrent(pathname, item.to)}
      // The accent fill is what a sighted user sees; the `aria-current` above is what a screen
      // reader announces. ⚠️ The CONDITION is still `NavLink`'s alone — the prop above supplies only
      // the VALUE — so there is still exactly ONE source deciding whether the attribute appears, and
      // ⛔ no second `aria-current` inside the anchor, which would announce twice.
      className={({ isActive }) =>
        `${RAIL_ITEM_BASE_CLASS} ${open ? RAIL_ITEM_OPEN_PARENT_CLASS : isActive ? RAIL_ITEM_SELECTED_CLASS : RAIL_ITEM_UNSELECTED_CLASS}`
      }
    >
      {body}
    </NavLink>
  );
}

/** One row of a scope picker. ⛔ `to` arrives already built — the rail constructs no paths. */
export interface RailScopeChoice {
  readonly id: string;
  readonly name: string;
  /** The second line of the row: an app's slug, or a data plane's region. */
  readonly subject: string;
  readonly to: string;
  /**
   * The suffix of the row test id `rail-app-picker-item-<key>`. Omitted, it is `subject`. A data
   * plane passes its id, because two data planes can share a region.
   */
  readonly key?: string;
}

/**
 * How a picker row lays out its name and subject.
 *
 * - `inline`: one line, the subject at the right of the name. The app rows use it.
 * - `stacked`: two lines, the subject under the name, both truncated. The data plane rows use it,
 *   because a region such as `northamerica-northeast2` would take the width from the name.
 */
export type RailScopeRowLayout = 'inline' | 'stacked';

/**
 * Everything the app picker needs, or `null` for a NON-INTERACTIVE chip.
 *
 * ⛔ **THIS REPLACED `scopeHref: string | null`, AND IT IS NOT A WIDENING FOR ITS OWN SAKE.** The href
 * form could only express *"go to the catalogue"*, which is why the chip shipped as a link wearing a
 * chevron. A picker needs the list, so the list is the prop.
 *
 * ⚠️ **`null` KEEPS BOTH OF THE OLD MEANINGS EXACTLY**, and they are different facts that agree on the
 * answer: fewer than two reachable apps (*"a control that opens a list of one is worse than a label"*,
 * §4.2 — this package's third ruling of that shape), and the list not having arrived (a chip that
 * becomes a control a moment later is a control appearing under the cursor — `absent → available`,
 * the sibling of `absent → denied`).
 */
export interface RailScopePicker {
  /**
   * Every choice the principal can reach, in the server's order. The app picker list always holds
   * the current app. The data plane picker list holds only in-service data planes, so it can hold no
   * row for the current data plane (plan 2026-10-14-console-dp-picker, D8).
   */
  readonly choices: readonly RailScopeChoice[];
  /** The layout of the choice rows. Omitted, it is `inline`. The "all" row is always one line. */
  readonly rowLayout?: RailScopeRowLayout;
  /**
   * The id of the current record. The row with this id is marked current. When `choices` holds no
   * such row, as a data plane picker can (D8), the picker marks no row.
   */
  readonly currentId: string;
  /** Where "leave this scope entirely" goes. ⭐ Here because leaving IS a scope decision. */
  readonly allTo: string;
  /** ⛔ The label for that row — `All apps`, later `All tenants`. Never hard-coded in the rail. */
  readonly allLabel: string;
  /**
   * `true` adds a filter field over the rows, matched on name and subject. Omitted, no field. The
   * app picker sets it for a long list. The data plane picker sets it because every name starts
   * with `dp-`, so a one-character type-ahead finds no data plane.
   */
  readonly searchable?: boolean;
}

/**
 * ⭐⭐ **THE RECORD IN SCOPE — ONE DESCRIPTOR, ANY KIND OF RECORD** (R9-13).
 *
 * ⛔ **THIS REPLACED `appScope` + `scopedItems` + `scopePicker`, AND THE COLLAPSE IS THE POINT.**
 * Those three props were only ever meaningful together and were all app-shaped: `AppScopeSummary` was
 * `{ name, slug }`, the group label hard-coded the word `App`, and the picker's fields were
 * `currentAppId` / `allAppsTo`. So the owner's stated precedent — *"we are now setting a precedent
 * that we need to follow for any top level choice like that, specifically tenants and data
 * planes"* — could not be followed without editing this file for every adopter.
 *
 * ⇒ The acceptance test the lead set, and the one to re-apply before changing anything here: **if you
 * can imagine the tenant scope needing a `NavRail` edit, the generalisation is wrong.** A tenant scope
 * is `{ label: 'Tenant', title: org.name, subject: org.slug, items, picker }` and nothing else.
 */
export interface RailScope {
  /**
   * The group label's leading word — `App`, `Data plane`. Rendered as `<label> · <subject|title>`.
   * ⛔ Comes from the adopter (the manifest's `DetailScope.label`), never from a `kind` switch here.
   */
  readonly label: string;
  /** The chip's first line: the record's human name. ⚠️ Callers fall back to the id, never to `''`. */
  readonly title: string;
  /**
   * The short machine handle — an app's slug. ⛔ **OMITTED means "there isn't one worth showing"**,
   * and it is a real state rather than a missing value: a data plane's only handle is a 36-character
   * UUID, and spending the chip's second line *and* the group label on it buys nothing an operator
   * reads at a glance (the id is in the page heading, in mono, where it is copied from).
   *
   * ⚠️ When omitted the chip renders ONE line and the group label falls back to {@link title}.
   */
  readonly subject?: string;
  /**
   * This scope's destinations. ⛔ A SEPARATE region from `globalItems` rather than a marker on them:
   * the scoped set is a **sibling with its own landmark**, and folding it into a section group is
   * precisely the nesting N-5 refused.
   */
  readonly items: readonly RailItem[];
  /**
   * The picker, or `null` for a NON-INTERACTIVE chip. @see RailScopePicker
   *
   * ⛔ **`null` IS THE ONE-CHOICE CASE, AND IT IS A RULING, NOT AN OPTIMISATION** (§4.2): *"a control
   * that opens a list of one is worse than a label"* (`SectionHeader`'s original refusal), and this
   * package has established the same rule twice already — `MIN_TABS` (*"a one-tab tablist is a control
   * offering no choice"*) and the section strip's suppression at one section. ⚠️ It is also the
   * list-not-yet-arrived arm, so the chip never mutates into a control under the operator's cursor.
   *
   * ⛔⛔ **AN EARLIER SHAPE WAS `scopeHref: string | null`, AND LEAD RULING Q4 IS REVERSED.** Q4
   * approved shipping the ≥2 case as *"a plain link to `/apps` for one commit"*, on the single
   * condition that `aria-haspopup` be omitted. It was — and the chip **kept its chevron**, so the
   * control *signified a menu* and *delivered a navigation*. ⭐ The owner caught it within minutes:
   * *"I would expect that dropdown to be an actual dropdown though..."* ⇒ **the condition guarded the
   * wrong channel**: honest to a screen reader, a lie to every sighted operator.
   *
   * ⛔ Route construction stays in `AppHost` — the rail builds no paths, same rule as `to`.
   */
  readonly picker: RailScopePicker | null;
}

export interface NavRailProps {
  /**
   * Every GLOBAL destination the principal can see, across all sections, in manifest declaration
   * order. Grouped here by {@link groupRailBySection}.
   */
  readonly globalItems: readonly RailItem[];
  /** The record in scope and its destinations, or `null` on every unscoped screen. */
  readonly scope: RailScope | null;
  /**
   * ⛔⛔ **REHOMED TO THE RAIL FOOTER FOR THE SECOND TIME, AND IT HAS NEARLY BEEN LOST TWICE** (R9
   * **N-8 trap 2**).
   *
   * *"Showing your only app. More apps appear here when you're granted them."* lived in this footer,
   * moved into `AppTabStrip` when the strip replaced the rail — caught then, because dropping the rail
   * without rehoming it would have silently deleted the note for exactly the one-app operator it was
   * written for — and **deleting the strip now moves it back**. Its test id returns to the original
   * `rail-single-app-note` (it was `app-strip-single-app-note` while it lived in the strip).
   *
   * ⚠️ Rendered ONLY in the single-app landing case, so it EXPLAINS the absence of a catalogue rather
   * than leaving it looking like an omission.
   */
  readonly showSingleAppNote: boolean;
}

/**
 * The app-context chip — *"a nice looking header in that LHS menu that shows me which app I've
 * chosen"*.
 *
 * ⛔ **NO FIXTURE COUNT AND NO NUMERIC BADGE OF ANY KIND**, for the reason recorded since R6: there is
 * no producer for it, so a hardcoded or zero value would be a lie sitting in the chrome — the single
 * worst place for one, because chrome is read as ambient truth rather than as data.
 */
function ScopeChip({ scope }: { readonly scope: RailScope }): React.JSX.Element {
  /*
   * ⚠️ The monogram and the text block, shared by the static chip and the picker trigger, so the chip
   * looks identical either way — only its AFFORDANCE differs. ⛔ No chevron in here: on the static
   * block there is nothing to open, and on the trigger `ActionMenu` owns it (it is the popup
   * affordance, and it belongs to whatever actually has the popup).
   *
   * ⛔ **THE TEST IDS ARE `rail-app-name` / `rail-app-slug` AND THEY STAY BYTE-IDENTICAL EVEN THOUGH
   * THE CHIP IS NO LONGER APP-ONLY — N-9**: *"Rename what a human reads; never what a gate names."*
   * `chrome.test.tsx` names both. The word `app` in a test id is now slightly wrong and that is
   * strictly cheaper than a rename that reds a gate whose subject did not change.
   */
  /* The record name and handle are server text, escaped once for the chip and the trigger (plan G8, R-7). */
  const title = displayUntrustedName(scope.title);
  const subject = scope.subject === undefined ? undefined : displayUntrustedName(scope.subject);
  const body = (
    <>
      <span aria-hidden className={RAIL_SCOPE_MONOGRAM_CLASS}>
        {appInitials(scope.title)}
      </span>
      <span className={RAIL_SCOPE_TEXT_CLASS}>
        <span className={RAIL_SCOPE_TITLE_CLASS} data-testid="rail-app-name">
          {title}
        </span>
        {/*
          ⛔ **ABSENT, NOT EMPTY, WHEN THERE IS NO SHORT HANDLE.** Rendering the span with `''` would
          leave a 14px gap under the name on every data-plane screen — an alignment difference that
          reads as a rendering bug rather than as "this record has no slug". @see RailScope.subject
        */}
        {subject === undefined ? null : (
          <span className={RAIL_SCOPE_SUBJECT_CLASS} data-testid="rail-app-slug">
            {subject}
          </span>
        )}
      </span>
    </>
  );
  return (
    <div className={RAIL_SCOPE_CLASS} data-testid="rail-scope">
      {scope.picker === null ? (
        /*
         * ⛔ A plain block: no `<button>`, no `<a>`, no chevron, no `aria-haspopup`. §4.2's "exactly 1"
         * row — *"a control that opens a list of one is worse than a label"*. ⚠️ This is also the arm
         * the list-not-yet-arrived case lands in, so the chip never mutates into a control under the
         * operator's cursor.
         * A data plane chip has a picker so an operator can open the same tab on another data plane.
         * It lands here only with fewer than two data planes or before the list arrives.
         */
        <div className={RAIL_SCOPE_STATIC_CLASS} data-testid="rail-scope-static">
          {body}
        </div>
      ) : (
        <ScopePicker scope={scope} picker={scope.picker} body={body} />
      )}
    </div>
  );
}

/**
 * ⭐⭐ **THE APP PICKER — `ActionMenu`, EXPANDED, NOT A SECOND MENU.**
 *
 * The owner, having been given a chip with a chevron that navigated to `/apps`: *"I would expect that
 * dropdown to be an actual dropdown though..."* ⛔ He is right, and the interim was wrong in a specific
 * way worth recording: it correctly OMITTED `aria-haspopup` (so it never lied to a screen reader) **and
 * kept the chevron** — so it was honest to AT and a lie to every sighted operator. ⇒ **a popup
 * affordance and a popup are one decision**; shipping either alone is the defect.
 *
 * ## ⛔ WHY `ActionMenu` AND NOT A NEW COMPONENT
 *
 * Everything hard here is already solved there and is **behaviour, not presentation**: the open/close
 * state machine, outside-click, and all three focus traps its header enumerates — conditional mount
 * (a `hidden` menu satisfies every reachability assertion while being unreachable), focus-restore
 * gated on a real open→close transition (this package has already shipped the mount-steals-focus
 * defect once), and the dialog-suppression arm. A second menu would re-derive all of it and get one
 * of them subtly different.
 *
 * What genuinely did not fit was **presentation**, and it was expanded rather than forked:
 * `triggerContent` (a chip is not a word), `tone="chrome"` (`default`'s hover fill measures 1.0000:1
 * on chrome — N-6 D-A), and `placement="stretch"` (`end`'s fixed `w-80` renders 320px of menu leftward
 * off-screen from a 216px container).
 *
 * ## ⭐ `role="menu"`, NOT `listbox` — and the deciding question is what activation DOES
 *
 * A `listbox` sets a value; a `menu` performs an action. Choosing an app here **navigates** — it is a
 * link, it changes the URL, it is middle-clickable — so `menuitem` is the honest role and
 * `aria-haspopup="menu"` the honest attribute. ⚠️ *"All apps"* settles any remaining doubt: it is not a
 * member of the value-set an app-picking listbox would offer, and it sits in the same list.
 *
 * ## ⭐ "ALL APPS" LIVES HERE, AND IT IS A REHOMING WITH A REASON
 *
 * The rail deliberately moved `All apps` OUT of the scoped group because keeping it inside *"implied it
 * was scoped to the app you are trying to leave."* The picker is its correct home: **switching app and
 * leaving app scope are one operator intent**, reached from one control. ⛔ It stays DISTINCT from the
 * rail's `App catalog` destination — two affordances, two labels, and reconciling them would put the
 * catalogue back inside a scope it is not part of.
 *
 * ## A menu with a filter field above it, opt-in through `RailScopePicker.searchable`
 *
 * The 2026-08-07 decision shipped a bare menu at 12 production apps, with APG character type-ahead
 * (`isTypeAheadKey`/`typeAheadIndex`), and set a revisit point at about 40 rows. The app picker and
 * the data plane picker now set `searchable`, so `ActionMenu` renders its `search` field and
 * `filterChoices` narrows the rows.
 *
 * The field is a sibling of the `role="menu"` element and never its child. So the control keeps one
 * menu implementation and needs no combobox + `listbox` + `aria-activedescendant` contract.
 * Type-ahead still works on the rows once focus leaves the field.
 */
function ScopePicker({
  scope,
  picker,
  body,
}: {
  readonly scope: RailScope;
  readonly picker: RailScopePicker;
  readonly body: React.ReactNode;
}): React.JSX.Element {
  /*
   * ⛔ **THE ACCESSIBLE NAMES ARE COMPOSED FROM `scope.label`, NOT THE WORD `app`.** They read
   * `"Switch app — currently Perf Lab"` today, byte-identical to what shipped, and a tenant picker
   * would read `"Switch tenant — currently Acme"` with no edit here. ⚠️ `toLowerCase()` because
   * `label` is a display word (`Data plane`) and this is mid-sentence.
   */
  const noun = scope.label.toLowerCase();
  const stacked = picker.rowLayout === 'stacked';
  const [query, setQuery] = useState('');
  const choices = picker.searchable === true ? filterChoices(picker.choices, query) : picker.choices;
  return (
    <ActionMenu
      id="rail-app-picker-menu"
      testId="rail-app-picker"
      /*
       * ⛔ An explicit accessible name. Composed from the children it would be `"CR Crawler crawler"` —
       * the monogram is `aria-hidden`, but the name and slug are not and both are read.
       */
      label={`Switch ${noun} — currently ${displayUntrustedName(scope.title)}`}
      menuLabel={`Switch ${noun}`}
      triggerContent={body}
      tone="chrome"
      placement="stretch"
      search={
        picker.searchable === true
          ? {
              value: query,
              onChange: setQuery,
              label: `Filter ${noun}s`,
              status: query.trim() === '' ? '' : matchStatus(choices.length, noun),
              empty: choices.length === 0,
            }
          : undefined
      }
    >
      {choices.map((choice) => {
        /* Each row's name and subject are server text, escaped once per row (plan G8, R-7). */
        const name = displayUntrustedName(choice.name);
        const subject = displayUntrustedName(choice.subject);
        return (
        <ScopePickerItem
          key={choice.id}
          to={choice.to}
          stacked={stacked}
          // Both lines of a stacked row truncate, so the title carries the full name and subject.
          title={stacked ? `${name} (${subject})` : undefined}
          testId={`rail-app-picker-item-${choice.key ?? choice.subject}`}
          typeAhead={name}
          /*
           * ⭐ `aria-current="true"`, not `"page"`, and for the same reason `railAriaCurrent` exists:
           * this item points at the record's ROOT while the operator is on one of its inner screens,
           * so `"page"` would name a page they are not on. ⚠️ It is also the only non-colour channel
           * marking the current row in the list.
           */
          current={choice.id === picker.currentId}
        >
          <span className={stacked ? RAIL_PICKER_STACKED_NAME_CLASS : RAIL_PICKER_NAME_CLASS}>
            {name}
          </span>
          <span className={stacked ? RAIL_PICKER_STACKED_SUBJECT_CLASS : RAIL_PICKER_SUBJECT_CLASS}>
            {subject}
          </span>
        </ScopePickerItem>
        );
      })}
      {/*
        ⛔ A separator, because "All apps" is a DIFFERENT KIND of thing from the apps above it — it
        leaves scope rather than changing it. `separator` is one of the four roles a `menu` may
        contain, so this is the sanctioned way to say so.

        ⚠️ **`<hr>`, NOT `<div role="separator" />`, and it is not only about redundant ARIA.** `hr`
        maps natively to the `separator` role, so the attribute would be redundant — but the deciding
        reason is mechanical: `prose-measure.test.ts` RATCHET A hunts a closing `</div>` and a
        SELF-CLOSING `<div />` has none, so it swallows everything up to the next one and blames this
        element for **361 characters** of someone else's prose (its header calls this artifact B2).
        Measured 2026-08-07 — it red a lowerable-only ceiling. ⛔ Do not restore the div form.
      */}
      {/* When the filter leaves no app row, the separator divides nothing, so the picker omits it. */}
      {choices.length > 0 && <hr className={RAIL_PICKER_SEPARATOR_CLASS} />}
      <ScopePickerItem
        to={picker.allTo}
        stacked={false}
        testId="rail-app-picker-all"
        typeAhead={picker.allLabel}
        current={false}
      >
        <span className={RAIL_PICKER_ALL_CLASS}>{picker.allLabel}</span>
      </ScopePickerItem>
    </ActionMenu>
  );
}

/** The polite status of a filter: the count of rows it left, or that none match. */
function matchStatus(count: number, noun: string): string {
  if (count === 0) return `No ${noun}s match`;
  return count === 1 ? `1 ${noun}` : `${count} ${noun}s`;
}

/** The rows whose name or subject holds the text. The match ignores case and the outer spaces. */
function filterChoices(choices: readonly RailScopeChoice[], query: string): readonly RailScopeChoice[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return choices;
  return choices.filter(
    (choice) => choice.name.toLowerCase().includes(needle) || choice.subject.toLowerCase().includes(needle),
  );
}

/**
 * One row of the picker.
 *
 * ⛔ **`role="menuitem"` ON A `<Link>`, and `tabIndex={-1}`, both required by `ActionMenu`** — it finds
 * its items by role and drives focus itself, and its header states it does NOT clone children to add
 * them. ⚠️ A real `<Link>` rather than a button-that-navigates, so middle-click and "open in new tab"
 * work; the role changes how it is *announced*, not what it *is*.
 */
function ScopePickerItem({
  to,
  stacked,
  title,
  testId,
  typeAhead,
  current,
  children,
}: {
  readonly to: string;
  /** True for the two-line row. @see RailScopeRowLayout */
  readonly stacked: boolean;
  /** The full text of the row for a pointer hover, or `undefined` for none. */
  readonly title?: string;
  readonly testId: string;
  /** ⛔ What a keystroke matches — the record NAME, never the scraped row text. @see TYPEAHEAD_ATTR */
  readonly typeAhead: string;
  readonly current: boolean;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  /*
   * A pick closes the menu, and the link navigates. A pick of a record row gives focus back to the
   * trigger. A pick of the "all" row leaves the scope, so the chip and its trigger unmount.
   */
  const closeMenu = useCloseMenu();
  return (
    <Link
      to={to}
      onClick={closeMenu}
      title={title}
      role="menuitem"
      tabIndex={-1}
      data-testid={testId}
      {...{ [TYPEAHEAD_ATTR]: typeAhead }}
      aria-current={current ? 'true' : undefined}
      className={`${stacked ? RAIL_PICKER_STACKED_ITEM_BASE_CLASS : RAIL_PICKER_ITEM_BASE_CLASS} ${
        current ? RAIL_PICKER_ITEM_CURRENT_CLASS : RAIL_PICKER_ITEM_RESTING_CLASS
      }`}
    >
      {children}
    </Link>
  );
}

/** The one section that collapses: Settings, the lowest-frequency group (plan D1). */
const COLLAPSIBLE_SECTION: ShellSection = 'settings';

/** The id of a group's title, which names its list through `aria-labelledby`. */
function groupLabelId(section: ShellSection): string {
  return `rail-group-${section}-label`;
}

/** A labelled list of rail links: every global group and the scoped group render through it. */
function GroupList({
  labelledBy,
  items,
  id,
  listRef,
}: {
  readonly labelledBy: string;
  readonly items: readonly RailItem[];
  readonly id?: string;
  readonly listRef?: React.Ref<HTMLUListElement>;
}): React.JSX.Element {
  return (
    <ul id={id} ref={listRef} aria-labelledby={labelledBy} className={RAIL_LIST_CLASS}>
      {railBranches(items).map((branch) => (
        <RailBranchItem key={branch.item.id} branch={branch} />
      ))}
    </ul>
  );
}

/**
 * One top-level rail item. A parent shows its children, indented, while the current page is the
 * parent's or a child's; a press on the parent lands on its first child, so the press opens them.
 */
function RailBranchItem({ branch }: { readonly branch: RailBranch }): React.JSX.Element {
  const { pathname } = useLocation();
  const { item, children } = branch;
  if (children.length === 0) {
    return (
      <li>
        <RailLink item={item} />
      </li>
    );
  }
  const open = railItemLit(item, pathname) || children.some((child) => railItemLit(child, pathname));
  const listId = `rail-children-${item.id}`;
  return (
    <li>
      <RailLink item={item} expanded={open} controls={listId} />
      {open ? (
        <ul id={listId} aria-label={displayUntrustedName(item.displayName)} data-testid={listId} className={RAIL_CHILD_LIST_CLASS}>
          {children.map((child) => (
            <li key={child.id}>
              <RailLink item={child} nested />
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/** A static section: a labelled `<div>` title over its list, with no control. */
function StaticGroup({ group, boxClass }: { readonly group: RailGroup; readonly boxClass: string }): React.JSX.Element {
  return (
    <div data-testid={`rail-group-${group.section}`} className={boxClass}>
      <div id={groupLabelId(group.section)} className={GROUP_LABEL_CLASS}>
        {group.label}
      </div>
      <GroupList labelledBy={groupLabelId(group.section)} items={group.entries} />
    </div>
  );
}

/**
 * Settings as a plain disclosure button. Closed, the list is not rendered. The open state is React
 * state only (N-7); the route opens it on entry to Settings and closes it on exit (plan R5).
 */
function CollapsibleGroup({
  group,
  boxClass,
}: {
  readonly group: RailGroup;
  readonly boxClass: string;
}): React.JSX.Element {
  const { pathname } = useLocation();
  // The rule the rail uses to light a link.
  const onGroupPage = group.entries.some((item) => railItemLit(item, pathname));
  const [open, setOpen] = useState(onGroupPage);
  const [seenOnGroupPage, setSeenOnGroupPage] = useState(onGroupPage);
  const scrollOnMount = useRef(false);
  // The route crossed the group boundary, so the open state follows the route.
  if (seenOnGroupPage !== onGroupPage) {
    setSeenOnGroupPage(onGroupPage);
    setOpen(onGroupPage);
  }
  const labelId = groupLabelId(group.section);
  const listId = `rail-group-${group.section}-list`;
  const currentId = `rail-group-${group.section}-current`;
  const showCurrent = !open && onGroupPage;
  const chevronClass = open
    ? `${RAIL_DISCLOSURE_CHEVRON_CLASS} ${RAIL_DISCLOSURE_CHEVRON_OPEN_CLASS}`
    : RAIL_DISCLOSURE_CHEVRON_CLASS;
  // An operator open scrolls the new list into view, so links below the fold do not look absent.
  const scrollIntoViewOnOpen = (node: HTMLUListElement | null): void => {
    if (node === null || !scrollOnMount.current) return;
    scrollOnMount.current = false;
    node.scrollIntoView({ block: 'nearest' });
  };
  return (
    <div data-testid={`rail-group-${group.section}`} className={boxClass}>
      <Button
        tone="disclosure"
        size="micro"
        id={labelId}
        aria-expanded={open}
        aria-controls={listId}
        aria-describedby={showCurrent ? currentId : undefined}
        onClick={() => {
          scrollOnMount.current = !open;
          setOpen(!open);
        }}
      >
        <span className={RAIL_DISCLOSURE_LABEL_CLASS}>{group.label}</span>
        {showCurrent ? (
          <span aria-hidden="true" data-testid="rail-settings-current" className={RAIL_DISCLOSURE_CURRENT_CLASS} />
        ) : null}
        <ChevronRight aria-hidden="true" className={chevronClass} />
      </Button>
      {/* `hidden` keeps the text out of the reading flow; `aria-describedby` still reads it. */}
      {showCurrent ? (
        <span id={currentId} hidden>
          The current page is in this section.
        </span>
      ) : null}
      {open ? (
        <GroupList id={listId} listRef={scrollIntoViewOnOpen} labelledBy={labelId} items={group.entries} />
      ) : null}
    </div>
  );
}

export function NavRail({ globalItems, scope, showSingleAppNote }: NavRailProps): React.JSX.Element {
  const groups = groupRailBySection(globalItems);
  /*
   * ⛔ **NOTHING IN SCOPE ⇒ NOTHING RENDERED** (§4.3), and the condition is `scope !== null && the
   * scoped set is non-empty` — BOTH halves.
   *
   * An always-present chip reading *"No app selected"* is chrome describing an absence, and it would
   * push every real destination down on every screen to say nothing. ⚠️ The second half is the arm
   * that is easy to miss: an operator inside a record with NO reachable destinations would otherwise
   * get a chip over an empty labelled group — and `SectionLayout.tsx:420-425` already measured what an
   * entry-less rail looks like (*"a full-height bordered empty column — the strongest 'this is broken,
   * or still loading' signal on the page"*). Do not reproduce that at group scale.
   */
  const inScope = scope !== null && scope.items.length > 0;
  return (
    <div
      /* ⛔ A `<div>`, NOT an `<aside>`, and NO landmark role — see this file's header. `w-60` = 240px,
         re-derived; `shrink-0` because the rail never yields to content (it yields to a drawer, N-d). */
      className={RAIL_ROOT_CLASS}
      data-testid="section-rail"
    >
      {inScope ? <ScopeChip scope={scope} /> : null}
      {/* The only scrolling element in the rail. `min-h-0` lets it shrink below its content, so it
          scrolls, and the page does not, when the rail is taller than the window. */}
      <div className={RAIL_SCROLL_CLASS} data-testid="rail-scroll">
        {inScope ? (
          <nav
            /* LANDMARK 1. ⛔ `aria-labelledby` on the VISIBLE label, so the announced name and the
               rendered text have one source. */
            aria-labelledby="rail-scoped-label"
            data-testid="rail-scoped"
            /* ⭐ Two signals — a 2px `border-strong` connector descending from the chip's own left
               edge, and a label that NAMES the record. @see RAIL_SCOPED_GROUP_CLASS for the removed
               wash and the four artifacts it cost. */
            className={RAIL_SCOPED_GROUP_CLASS}
          >
            <div
              id="rail-scoped-label"
              data-testid="rail-scoped-label"
              className={GROUP_LABEL_CLASS}
            >
              {/*
                ⛔ **`{scope.label}`, NOT THE LITERAL `App` — THIS IS THE LINE R9-13 EXISTED FOR.**
                The word was hard-coded, which is what made the whole scoped group app-shaped and a
                second adopter impossible without editing this file.

                ⚠️ The mono half falls back to `title` when there is no short handle: a data plane's
                only handle is a UUID, so it ships without one and the label reads
                `Data plane · dp-acme-eu` rather than `Data plane · 1111…`. @see RailScope.subject
              */}
              {scope.label} ·{' '}
              <span className={RAIL_SCOPED_SUBJECT_CLASS}>{displayUntrustedName(scope.subject ?? scope.title)}</span>
            </div>
            <GroupList labelledBy="rail-scoped-label" items={scope.items} />
          </nav>
        ) : null}
        {/* LANDMARK 2 — always. ⛔ `aria-label="Console"`, a static name: the rail no longer shows one
            section, so naming it after the active section would be false. */}
        <nav aria-label="Console" data-testid="rail-global">
          {groups.map((group, index) => {
            const boxClass = index === 0 ? RAIL_GROUP_CLASS : RAIL_GROUP_DIVIDED_CLASS;
            return group.section === COLLAPSIBLE_SECTION ? (
              <CollapsibleGroup key={group.section} group={group} boxClass={boxClass} />
            ) : (
              <StaticGroup key={group.section} group={group} boxClass={boxClass} />
            );
          })}
        </nav>
      </div>
      {/*
        ⛔ THE FOOTER'S STATIC `Control Plane` WORD IS DELETED. The top bar carries the product mark,
        and a second static product word at the bottom of the nav is chrome describing chrome.
        ⚠️ So the footer renders NOTHING unless there is something to say — an empty bordered strip at
        the foot of the rail would be the `Control Plane` word's defect without even the word.
      */}
      {showSingleAppNote ? (
        <div className={RAIL_FOOTER_CLASS} data-testid="rail-footer">
          <p className={RAIL_FOOTER_NOTE_CLASS} data-testid="rail-single-app-note">
            Showing your only app. More apps appear here when you&apos;re granted them.
          </p>
        </div>
      ) : null}
    </div>
  );
}
